import { useState } from 'react'
import Select, {
  components as DefaultComponents,
  GroupBase,
  StylesConfig,
} from 'react-select'

/**
 * Option type for the single select dropdown.
 */
export interface SelectOptionType {
  id?: number
  label: string
  value: string
  other?: any
  name?: string
  __isNew__?: boolean
}

/**
 * Props for the SingleSelect component.
 */
export interface SingleSelectType {
  id: string
  placeholder: string
  value: SelectOptionType | null
  options: SelectOptionType[]
  setValue: (value: SelectOptionType | null) => void
  className?: string
  isLoading?: boolean
  isDisabled?: boolean
  noOptionsMessage?: string
  error?: boolean
  isClearable?: boolean
  components?: Partial<typeof DefaultComponents>
  styles?: StylesConfig<SelectOptionType, false, GroupBase<SelectOptionType>>
}

/**
 * Merges custom styles with default error styles.
 * Ensures error styling is applied consistently while allowing custom styles to be merged.
 *
 * @param {StylesConfig<SelectOptionType, false>} customStyles - Custom styles provided by the user.
 * @param {boolean} error - Indicates if error styling should be applied.
 * @returns {StylesConfig<SelectOptionType, false>} The merged styles configuration.
 */
const mergeStyles = (
  customStyles: StylesConfig<SelectOptionType, false> | undefined,
  error: boolean
): StylesConfig<SelectOptionType, false> => ({
  control: (base) => ({
    ...base,
    minHeight: '39px',
    height: '39px',
    padding: '0',
    margin: '0',
    fontSize: '14px',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: error ? 'red' : '#e0e0e0',
    boxShadow: 'none',
    '&:hover': {
      borderColor: error ? 'red' : '#e0e0e0',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 6px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '30px',
  }),
  option: (styles, { isDisabled, isSelected, isFocused }) => {
    return {
      ...styles,
      width: '100%',
      fontSize: '14px',
      color: 'black',
      background: isFocused ? '#f1f5f9' : isSelected ? '#ffffff' : '',
      cursor: isDisabled ? 'not-allowed' : 'default',
      ':hover': {
        backgroundColor: '#f1f5f9 !important',
      },
    }
  },
  ...customStyles,
})

/**
 * SingleSelect Component
 *
 * A reusable, customizable dropdown component built on `react-select`.
 * Allows selecting a single option from a list, with support for custom styles, error handling, and more.
 *
 * @component
 * @example
 * // Example Usage:
 * const options = [
 *   { id: 1, label: "Option 1", value: "option1" },
 *   { id: 2, label: "Option 2", value: "option2" },
 *   { id: 3, label: "Option 3", value: "option3" },
 * ];
 *
 *
 * const [selectedOption, setSelectedOption] = useState<SelectOptionType | null>(null);
 *
 * <SingleSelect
 *   id="example-select"
 *   placeholder="Choose an option"
 *   value={selectedOption}
 *   options={options}
 *   setValue={setSelectedOption}
 *   error={!selectedOption}
 *   noOptionsMessage="No options available"
 *   styles={{
 *     control: (base) => ({ ...base, backgroundColor: "#f8f8f8" }),
 *   }}
 * />;
 *
 * @param {string} id - Unique identifier for the select element.
 * @param {string} placeholder - Placeholder text for the dropdown.
 * @param {SelectOptionType | null} value - The currently selected option.
 * @param {SelectOptionType[]} options - List of available options to select from.
 * @param {Function} setValue - Callback function to handle selection changes.
 * @param {string} [className=""] - Additional class names for custom styling.
 * @param {boolean} [isLoading=false] - Indicates if the dropdown is loading options.
 * @param {boolean} [isDisabled=false] - Indicates if the dropdown is disabled.
 * @param {string} [noOptionsMessage="No options available"] - Custom message displayed when no options are available.
 * @param {boolean} [error=false] - Indicates if the dropdown should be styled with an error state.
 * @param {object} [components] - Custom components to override the default react-select components.
 * @param {object} [styles] - Custom styles to override the default react-select styles.
 * @param {boolean} [isClearable=false] -Indicates whether to provide the clearable option or not
 * @returns {JSX.Element} The rendered SingleSelect component.
 */

const SingleSelect = ({
  id,
  placeholder,
  value,
  options,
  setValue,
  isLoading = false,
  isDisabled = false,
  noOptionsMessage = 'No options available',
  error = false,
  components,
  className = '',
  styles,
  isClearable = false,
}: SingleSelectType) => {
  const [localSearchText, setLocalSearchText] = useState<string>('')

  const handleInputChange = (input: string) => {
    setLocalSearchText(input)
  }

  const handleValueChange = (selectedOption: SelectOptionType | null) => {
    setValue(selectedOption)
  }

  return (
    <Select
      tabIndex={20000}
      id={id}
      className={`${className} `}
      placeholder={placeholder}
      value={value?.id == -1 ? null : value}
      options={options}
      isSearchable={true}
      isClearable={isClearable}
      isLoading={isLoading}
      isDisabled={isDisabled}
      inputValue={localSearchText}
      onInputChange={(input) => handleInputChange(input)}
      onChange={(selectedOption) =>
        handleValueChange(selectedOption as SelectOptionType | null)
      }
      noOptionsMessage={() => noOptionsMessage}
      components={components}
      styles={mergeStyles(styles, error)}
    />
  )
}

export default SingleSelect
